import React, { useState, useEffect, useRef } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { Users, XCircle, CheckCircle } from 'lucide-react';

const GameScreen = () => {
  const [gameState, setGameState] = useState('lobby');
  const [usersCount, setUsersCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [timer, setTimer] = useState(0);
  const [results, setResults] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const { lastMessage } = useWebSocket();

  // Handle WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;

    if (lastMessage.type === 'game_state') {
      setGameState(lastMessage.state);
      if (lastMessage.users_count !== undefined) {
        setUsersCount(lastMessage.users_count);
      }
      
      if (lastMessage.state === 'playing' && videoRef.current) {
        videoRef.current.play().catch(e => console.log('Video play failed:', e));
      } else if (lastMessage.state !== 'playing' && videoRef.current) {
        videoRef.current.pause();
      }
    } else if (lastMessage.type === 'question') {
      setGameState('question');
      setCurrentQuestion(lastMessage.question_data);
      setTimer(lastMessage.question_data.timer);
      setResults(null);
      setCorrectAnswer(null);
      
      if (videoRef.current) {
        videoRef.current.pause();
      }
    } else if (lastMessage.type === 'results') {
      setResults({
        survivors: lastMessage.survivors_count,
        eliminated: lastMessage.eliminated_count
      });
      setCorrectAnswer(lastMessage.correct_answer);
    } else if (lastMessage.type === 'init' && lastMessage.game) {
      setGameState(lastMessage.game.state || 'lobby');
    }
  }, [lastMessage]);

  // Timer countdown
  useEffect(() => {
    if (gameState === 'question' && timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer(t => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameState]);

  return (
    <div className="fullscreen" data-testid="game-screen">
      <div className="wave-background" />
      
      {/* Lobby State */}
      {gameState === 'lobby' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="game-lobby">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-white font-['Outfit'] mb-6">
              Interactive Quiz
            </h1>
            <p className="text-xl md:text-2xl text-slate-400 mb-12">
              Scan QR code or join with your phone
            </p>
            
            {/* Placeholder QR */}
            <div className="w-48 h-48 bg-white rounded-2xl mx-auto mb-8 flex items-center justify-center">
              <div className="text-slate-800 text-center">
                <div className="text-4xl font-bold">QR</div>
                <div className="text-sm">Scan to Join</div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 text-2xl text-blue-400">
              <Users size={32} />
              <span data-testid="users-count">{usersCount} Players Joined</span>
            </div>
          </div>
        </div>
      )}

      {/* Playing State */}
      {gameState === 'playing' && (
        <div className="video-container" data-testid="video-container">
          <video
            ref={videoRef}
            src="/assets/demo-video.mp4"
            className="w-full h-full object-cover"
            muted
            playsInline
          />
          <div className="absolute bottom-8 left-8 operator-card px-6 py-3">
            <span className="text-green-400 font-semibold">PLAYING</span>
          </div>
        </div>
      )}

      {/* Paused State */}
      {gameState === 'paused' && (
        <div className="video-container" data-testid="video-paused">
          <video
            ref={videoRef}
            src="/assets/demo-video.mp4"
            className="w-full h-full object-cover"
            muted
            playsInline
          />
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-4xl font-bold text-white">PAUSED</div>
          </div>
        </div>
      )}

      {/* Question State */}
      {gameState === 'question' && currentQuestion && (
        <div className="video-container" data-testid="question-screen">
          <video
            ref={videoRef}
            src="/assets/demo-video.mp4"
            className="w-full h-full object-cover"
            muted
            playsInline
          />
          <div className="question-overlay">
            {/* Timer */}
            <div className="timer-circle mb-8" data-testid="question-timer">
              {timer}
            </div>

            {/* Question */}
            <h2 className="text-3xl md:text-5xl font-bold text-white font-['Outfit'] text-center mb-12 max-w-4xl">
              {currentQuestion.question}
            </h2>

            {/* Options */}
            <div className="grid grid-cols-2 gap-6 max-w-3xl w-full">
              {Object.entries(currentQuestion.options).map(([key, value]) => (
                <div
                  key={key}
                  className={`p-6 rounded-xl border-2 text-center transition-all ${
                    correctAnswer === key 
                      ? 'border-green-500 bg-green-500/20' 
                      : correctAnswer && correctAnswer !== key 
                        ? 'border-red-500/50 bg-red-500/10 opacity-50' 
                        : 'border-slate-600 bg-slate-800/50'
                  }`}
                  data-testid={`option-${key}`}
                >
                  <span className="text-2xl font-bold text-blue-400 mr-3">{key}</span>
                  <span className="text-xl text-white">{value}</span>
                </div>
              ))}
            </div>

            {/* Results */}
            {results && (
              <div className="mt-12 flex gap-8" data-testid="question-results">
                <div className="flex items-center gap-3 text-green-400">
                  <CheckCircle size={32} />
                  <span className="text-2xl font-bold">{results.survivors} Survivors</span>
                </div>
                <div className="flex items-center gap-3 text-red-400">
                  <XCircle size={32} />
                  <span className="text-2xl font-bold">{results.eliminated} Eliminated</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection indicator */}
      <div className="absolute top-4 right-4 operator-card px-4 py-2">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-blue-400" />
          <span className="text-white font-semibold">{usersCount}</span>
        </div>
      </div>
    </div>
  );
};

export default GameScreen;
